// ----------------------------------------------------------------------- externos
import { useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import * as XLSX from "xlsx";

// ----------------------------------------------------------------------- internos
import { traerPersonal } from "../services/rrhh";
import { crearLicencia, vacacionesCargadasDe } from "../services/licencias";
import { clasificar, ROTULO_ESTADO } from "../shared/vacacionesImport";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* P-182 (Natalia): cargar en bloque las vacaciones solicitadas desde su
 * planilla (.xls / .xlsx).
 *
 * ── SE PREVISUALIZA ANTES DE CARGAR, Y NO ES UN LUJO ────────────────────────
 *
 * El legajo no alcanza para saber de quién es la fila (el 9 de la planilla es
 * CORREA y en la nómina es PAULETTI), así que cada fila se empareja por legajo
 * Y apellido, y lo que no matchea NO se carga a ciegas: se muestra para que
 * Natalia lo mire. Cargar mal una vacación es cargársela al que no es.
 *
 * Sólo se insertan las filas «listas»; las de otra persona, sin persona, con
 * fechas al revés o ya cargadas quedan a la vista y afuera. Re-subir la misma
 * planilla no duplica: se deduplica por persona + día de salida.
 */

// La columna de cada dato. Se busca por palabra clave en las dos filas de
// encabezado (el título se parte en dos renglones); si no aparece, cae a la
// posición fija de la planilla de Natalia.
const CLAVES = {
  legajo: { palabras: ["LEGAJO"], fijo: 1 },
  nombre: { palabras: ["NOMBRE", "APELLIDO"], fijo: 2 },
  ausencia: { palabras: ["AUSENCIA"], fijo: 5 },
  salida: { palabras: ["SALIDA"], fijo: 6 },
  regreso: { palabras: ["REGRESO"], fijo: 7 },
};

function _norm(s) {
  return String(s ?? "").toUpperCase();
}

/* De la matriz de celdas a filas {legajo, nombre, ausencia, salida, regreso}.
 * Toma sólo las de VACACIONES. Tolera que las columnas se corran. */
function _filasDe(matriz) {
  const hIdx = matriz.findIndex((row) =>
    (row || []).some((c) => _norm(c).includes("LEGAJO")),
  );
  if (hIdx < 0) return [];
  const colDe = (clave) => {
    const { palabras, fijo } = CLAVES[clave];
    const ancho = (matriz[hIdx] || []).length;
    for (let i = 0; i < ancho; i++) {
      const txt = _norm(matriz[hIdx][i]) + " " + _norm((matriz[hIdx + 1] || [])[i]);
      if (palabras.some((p) => txt.includes(p))) return i;
    }
    return fijo;
  };
  const c = {
    legajo: colDe("legajo"), nombre: colDe("nombre"),
    ausencia: colDe("ausencia"), salida: colDe("salida"),
    regreso: colDe("regreso"),
  };
  const filas = [];
  for (let r = hIdx + 2; r < matriz.length; r++) {
    const row = matriz[r] || [];
    const nombre = String(row[c.nombre] ?? "").trim();
    const legajo = row[c.legajo];
    if (!nombre || legajo == null || String(legajo).trim() === "") continue;
    const aus = _norm(row[c.ausencia]);
    if (aus && !aus.includes("VACAC")) continue; // sólo vacaciones
    filas.push({
      legajo, nombre,
      salida: row[c.salida], regreso: row[c.regreso],
    });
  }
  return filas;
}

const ORDEN_ESTADO = [
  "lista", "duplicada", "otro_legajo", "sin_persona", "ambiguo",
  "sin_legajo", "falta_salida", "falta_regreso", "regreso_no_posterior",
];

const VacacionesImportar = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos !== null && permisos.includes(p);
  const puedeVer = puede("ver.rrhh");
  const puedeCargar = puede("cargar.rrhh");

  const [items, setItems] = useState(null); // null = sin archivo
  const [nombreArch, setNombreArch] = useState("");
  const [estado, setEstado] = useState(""); // "" | leyendo | yendo | mal:… | ok:…
  const [progreso, setProgreso] = useState(null);

  const resumen = useMemo(() => {
    const r = {};
    for (const it of items || []) r[it.estado] = (r[it.estado] || 0) + 1;
    return r;
  }, [items]);
  const listas = (items || []).filter((it) => it.estado === "lista");

  async function onArchivo(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setNombreArch(f.name);
    setEstado("leyendo");
    setItems(null);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      // La hoja de vacaciones: la que se llama Hoja1, o cualquiera cuyo
      // contenido tenga «VACACIONES SOLICITADAS»; si no, la primera.
      let hoja = wb.SheetNames.find((n) => n.toUpperCase() === "HOJA1");
      if (!hoja) {
        hoja = wb.SheetNames.find((n) => {
          const m = XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1 });
          return m.slice(0, 3).some((row) =>
            (row || []).some((c) => _norm(c).includes("VACACIONES SOLICITADAS")),
          );
        });
      }
      hoja = hoja || wb.SheetNames[0];
      const matriz = XLSX.utils.sheet_to_json(wb.Sheets[hoja], { header: 1 });
      const filas = _filasDe(matriz);
      if (!filas.length) {
        setEstado("mal:No encontré filas de vacaciones en la planilla.");
        return;
      }
      const personal = await traerPersonal();
      const porLegajo = {};
      for (const p of personal || []) {
        const k = String(p.legajo ?? "").trim();
        if (!k) continue;
        (porLegajo[k] = porLegajo[k] || []).push(p);
      }
      // Primero se empareja para saber a qué DNIs mirarles lo ya cargado.
      const pre = filas.map((fila) => ({ fila, c: clasificar(fila, porLegajo, null) }));
      const dnis = pre.map((x) => x.c.dni).filter(Boolean);
      const ya = await vacacionesCargadasDe(dnis);
      const final = filas.map((fila) => {
        const c = clasificar(fila, porLegajo, ya);
        return {
          fila, ...c,
          persona: c.persona
            ? `${c.persona.apellido || ""}, ${c.persona.nombres || ""}`.trim()
            : "",
        };
      });
      setItems(final);
      setEstado("");
    } catch (err) {
      setEstado("mal:No pude leer la planilla. ¿Es el .xls de vacaciones?");
    }
  }

  async function cargar() {
    if (!listas.length) return;
    setEstado("yendo");
    setProgreso({ hechas: 0, total: listas.length, errores: 0 });
    const hoy = new Date().toISOString().slice(0, 10);
    let hechas = 0, errores = 0;
    const okDni = new Set();
    for (const it of listas) {
      try {
        await crearLicencia({
          dni: it.dni, tipo: "vacaciones",
          desde: it.desde, hasta: it.hasta,
          nota: `Vacaciones solicitadas (carga masiva ${hoy})`,
        });
        hechas += 1;
        okDni.add(`${it.dni}|${it.desde}`);
      } catch {
        errores += 1;
      }
      setProgreso({ hechas: hechas + errores, total: listas.length, errores });
    }
    // Las que entraron pasan a «duplicada» para que no se puedan cargar de nuevo.
    setItems((prev) =>
      (prev || []).map((it) =>
        it.estado === "lista" && okDni.has(`${it.dni}|${it.desde}`)
          ? { ...it, estado: "duplicada" }
          : it,
      ),
    );
    setEstado(errores ? `ok:Cargué ${hechas}. ${errores} fallaron, probá de nuevo.`
                      : `ok:Listo, cargué ${hechas} vacaciones.`);
  }

  /* ESTA PANTALLA ES DE CARGA Y NADA MÁS: hasta que no se sube la planilla no
   * hay nada para mirar. Así que al que puede ver y no cargar no le queda una
   * pantalla a medias: la puerta está cerrada, igual que para el que no puede
   * ver. (El enlace que trae hasta acá, en Vacaciones, ya sale sólo para quien
   * carga.) */
  if (permisos !== null && !(puedeVer && puedeCargar)) {
    return <PuertaCerrada />;
  }

  return (
    <>
      <p className="sub">
        <Link className="rto-link" to="/rrhh/vacaciones">← Vacaciones</Link>
      </p>
      <h2 style={{ margin: "0 0 4px" }}>Cargar vacaciones desde la planilla</h2>
      <p className="sub">
        Subí el <b>.xls de vacaciones solicitadas</b>.
      </p>

      {puedeCargar && (
        <div className="tk-filtros">
          <input
            type="file" accept=".xls,.xlsx"
            onChange={onArchivo}
            aria-label="Planilla de vacaciones"
          />
          {nombreArch && <span className="tk-chip">{nombreArch}</span>}
        </div>
      )}

      {estado === "leyendo" && <p className="sub">Leyendo la planilla…</p>}
      {estado.startsWith("mal:") && (
        <p className="aduana-error">{estado.slice(4)}</p>
      )}
      {estado.startsWith("ok:") && (
        <p className="ok-aviso">{estado.slice(3)}</p>
      )}

      {items && (
        <>
          <div className="tk-filtros" style={{ marginTop: 10 }}>
            <span className="tk-chip">
              {items.length} fila{items.length !== 1 ? "s" : ""}
            </span>
            {ORDEN_ESTADO.filter((e) => resumen[e]).map((e) => (
              <span key={e} className="tk-chip" title={ROTULO_ESTADO[e]}>
                {ROTULO_ESTADO[e]}: <b>{resumen[e]}</b>
              </span>
            ))}
          </div>

          {puedeCargar && (
            <button
              className="btn-primario"
              disabled={!listas.length || estado === "yendo"}
              onClick={cargar}
              style={{ margin: "8px 0" }}
            >
              {estado === "yendo" && progreso
                ? `Cargando ${progreso.hechas}/${progreso.total}…`
                : `Cargar ${listas.length} lista${listas.length !== 1 ? "s" : ""}`}
            </button>
          )}

          <div className="tabla-wrap">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Estado</th><th>Persona (planilla)</th><th>Legajo</th>
                  <th>Empareja con</th><th>Salida</th><th>Último día</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td>
                      <span className={"pill " + (
                        it.estado === "lista" ? "ok"
                          : it.estado === "duplicada" ? "gris" : "mal")}>
                        {ROTULO_ESTADO[it.estado] || it.estado}
                      </span>
                    </td>
                    <td>{it.fila.nombre}</td>
                    <td className="dato">{String(it.fila.legajo)}</td>
                    <td>{it.persona || "—"}</td>
                    <td className="dato">{it.desde || "—"}</td>
                    <td className="dato">{it.hasta || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

export default VacacionesImportar;
